// feedAlignment - keep POV switches on the same race clock.
//
// Every POV of a race is an independent HLS stream, so a switch would
// otherwise join the incoming feed at its OWN live edge, seconds away from
// the moment the viewer was just watching. The relay stamps every segment
// with #EXT-X-PROGRAM-DATE-TIME off a single host clock (measured skew
// between concurrent feeds: ~1ms), which makes wall clock the shared race
// timeline: compute where "the moment the outgoing feed is showing" lands on
// the incoming feed's timeline and start there instead.
//
// Alignment is ADVISORY. A missing clock, a playlist without PDT, a
// degenerate window, or any thrown error resolves to null and the switch
// proceeds with the default live-edge join. Nothing here may block a switch.
//
// Deliberately dependency-free (no hls.js import) so it stays unit-testable
// in plain node; the player wires these into Hls events itself.
//
// Facts this leans on (verified against hls.js 1.6.16 and a live relay
// manifest):
// - frag.programDateTime is ms-epoch, and hls.js backfills it onto every
//   fragment once any PDT tag exists (m3u8-parser backfillProgramDateTimes),
//   even though the relay only tags the newest segments.
// - With lowLatencyMode:false the rate-based catch-up is INERT (the latency
//   controller early-returns), so a feed STAYS wherever it starts - which is
//   exactly why matching the outgoing position works, and why clamping to
//   liveSyncPosition would only manufacture a backward jump.
// - The relay pads a young stream's window with #EXT-X-GAP segments;
//   starting inside one buffers black, so gaps bound nothing here.

// One real segment of protection against the window sliding (2s cadence)
// while the incoming feed buffers. The playlist TARGETDURATION reads 4 (the
// muxer rounds up from its spin-up segments) but rotation is one 2s segment
// per refresh, and a full targetduration margin would clamp the common case.
const LOWER_MARGIN_S = 2;

// Buffer-health cushion at the live edge: never start with less than two
// target durations of loadable media ahead.
const UPPER_MARGIN_TARGETDURATIONS = 2;

// refineAlignmentBeforeFlip only chases the outgoing clock across small,
// already-buffered distances; anything larger is a real clamp/stall story
// that a blind seek would turn into a spinner.
const REFINE_MIN_S = 0.25;
const REFINE_MAX_S = 4;
const REFINE_HEADROOM_S = 0.5;

// Wall-clock Date of the frame a feed is showing right now, or null.
// Prefers hls.js's PDT-derived playingDate; falls back to Safari's native
// getStartDate() when there is no Hls instance.
export function sampleFeedClock(hls, videoEl) {
  try {
    if (hls) {
      const d = hls.playingDate;
      if (d && isFinite(d.getTime())) return d;
    }
    if (videoEl && typeof videoEl.getStartDate === 'function') {
      const start = videoEl.getStartDate();
      const startMs = start && start.getTime ? start.getTime() : NaN;
      if (isFinite(startMs) && isFinite(videoEl.currentTime)) {
        return new Date(startMs + videoEl.currentTime * 1000);
      }
    }
  } catch (e) { /* advisory */ }
  return null;
}

// Map a wall-clock Date onto an hls.js LevelDetails timeline.
// Returns a media position (seconds) to hand to hls.startLoad(pos), or null
// for "use the default join". Pure - no hls.js types beyond the shape of
// details ({ live, hasProgramDateTime, targetduration, edge, fragments[] }).
export function computeAlignedStartPosition(targetDate, details) {
  try {
    if (!targetDate || !details) return null;
    if (!details.live || !details.hasProgramDateTime) return null;
    const targetMs = typeof targetDate.getTime === 'function' ? targetDate.getTime() : NaN;
    if (!isFinite(targetMs)) return null;
    const frags = details.fragments;
    if (!frags || !frags.length) return null;

    const targetduration = details.targetduration || 2;
    let firstReal = null;
    for (let i = 0; i < frags.length; i++) {
      if (!frags[i].gap) { firstReal = frags[i]; break; }
    }
    if (!firstReal) return null;

    const lower = firstReal.start + LOWER_MARGIN_S;
    const upper = details.edge - UPPER_MARGIN_TARGETDURATIONS * targetduration;
    if (!isFinite(lower) || !isFinite(upper) || upper <= lower) return null;

    // Newest-first: the target sits near the edge. -Infinity = the target
    // predates the window entirely; the lower clamp turns that into the
    // smallest possible FORWARD jump (the history no longer exists, and a
    // backward jump would visually un-happen race events).
    let pos = -Infinity;
    for (let i = frags.length - 1; i >= 0; i--) {
      const f = frags[i];
      if (f.gap || typeof f.programDateTime !== 'number') continue;
      if (f.programDateTime <= targetMs) {
        pos = f.start + Math.min((targetMs - f.programDateTime) / 1000, f.duration);
        break;
      }
    }

    const clamped = Math.min(Math.max(pos, lower), upper);
    return isFinite(clamped) ? clamped : null;
  } catch (e) {
    return null;
  }
}

// Fine correction at flip time: the outgoing feed kept playing while the
// incoming one buffered, so the incoming frame is typically a second or two
// behind the coarse-aligned target by the time it paints. If the difference
// is small and the incoming buffer already covers it, hop forward in-buffer.
// Returns true if it seeked (the caller should wait for the post-seek frame
// before revealing), false otherwise. Best-effort; never throws.
export function refineAlignmentBeforeFlip(incomingHls, incomingVideo, getTargetDate) {
  try {
    if (!incomingHls || !incomingVideo || typeof getTargetDate !== 'function') return false;
    const target = getTargetDate();
    const playing = incomingHls.playingDate;
    if (!target || !playing) return false;
    const deltaS = (target.getTime() - playing.getTime()) / 1000;
    if (!(deltaS >= REFINE_MIN_S && deltaS <= REFINE_MAX_S)) return false;
    const want = incomingVideo.currentTime + deltaS;
    const buf = incomingVideo.buffered;
    for (let i = 0; i < buf.length; i++) {
      if (want >= buf.start(i) && want + REFINE_HEADROOM_S <= buf.end(i)) {
        incomingVideo.currentTime = want;
        return true;
      }
    }
  } catch (e) { /* advisory */ }
  return false;
}
